import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { COLORS } from "../config/themes";
import Icon from "../components/Icon";
import { Badge, Avatar, Button, Modal, InputField, SelectField, StatCard, ExportButtons, TimePicker, AnimatedNumber } from "../components";
import { todayStr, exportToCSV, exportToExcel, studentName, ageGroupColor, ageGroupBg, canEdit, canEditAttendance } from "../utils/helpers";
import { validate, required, optionalEmail, optionalPhone } from "../utils/validators";
import { FileText } from "../components/Icon";
import { ENROLLMENT_STAGES } from "../config/constants";
import { AGE_GROUPS } from "../config/constants";
import { supabase } from "../supabaseClient";

const EnrollStatusBadge = ({ status }) => {
  const stage = ENROLLMENT_STAGES.find(s => s.id === status);
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: stage?.color + "18", color: stage?.color }}>{stage?.icon ? <Icon name={stage.icon} size={12} /> : null} {stage?.label}</span>;
};

const EnrollDocCheck = ({ label, checked, onToggle, editable }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: editable ? "pointer" : "default" }}>
    <input type="checkbox" checked={checked} onChange={onToggle} disabled={!editable} style={{ accentColor: COLORS.accent, width: 15, height: 15 }} />
    <span style={{ color: checked ? COLORS.accent : COLORS.textMuted, fontWeight: checked ? 700 : 500 }}>{checked ? "✓" : "○"} {label}</span>
  </label>
);

const EnrollFormSection = ({ title, children }) => (<div style={{ marginBottom: 16 }}><div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 10, letterSpacing: 0.5 }}>{title}</div>{children}</div>);

const EnrollmentPage = memo(({ apps, setApps, classrooms, students, setStudents, currentUser, schoolId }) => {
  const isMobile = useIsMobile();
  const editable = !!setApps;
  const [view, setView] = useState("pipeline");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [noteText, setNoteText] = useState("");
  const [toast, setToast] = useState("");
  const [dragId, setDragId] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareSending, setShareSending] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [inviteTarget, setInviteTarget] = useState(null); // { name, email, appId }
  const [inviteSending, setInviteSending] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const enrollmentFormUrl = schoolId ? `${window.location.origin}/?enroll=1&school=${schoolId}` : null;

  const copyEnrollLink = () => {
    if (!enrollmentFormUrl) return;
    navigator.clipboard.writeText(enrollmentFormUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  const openInvite = (app) => {
    setInviteTarget({
      name: app.parentName || "",
      email: app.parentEmail || "",
      appId: app.id,
      childName: `${app.childFirst} ${app.childLast}`,
    });
    setShowInviteModal(true);
  };

  const sendShareEmail = async () => {
    if (!shareEmail.trim() || !enrollmentFormUrl) return;
    setShareSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/send-enrollment-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
        body: JSON.stringify({
          to: shareEmail.trim().toLowerCase(),
          enrollmentUrl: enrollmentFormUrl,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setShareSuccess(true);
        setToast(`Enrollment form sent to ${shareEmail.trim()}`);
        setTimeout(() => { setToast(""); setShowShareModal(false); setShareEmail(""); setShareSuccess(false); }, 3000);
      } else {
        throw new Error(data.error || "Send failed");
      }
    } catch (e) {
      setToast("Failed to send email. Try copying the link instead.");
      setTimeout(() => setToast(""), 4000);
    }
    setShareSending(false);
  };

  const sendInvite = async () => {
    if (!inviteTarget?.email || !inviteTarget?.name) return;
    setInviteSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${supabase.supabaseUrl}/functions/v1/invite-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
        body: JSON.stringify({ email: inviteTarget.email, name: inviteTarget.name, role: "parent" }),
      });
      const data = await res.json();
      if (data.success) {
        setToast(`Invite sent to ${inviteTarget.email}`);
        setTimeout(() => setToast(""), 3000);
        setShowInviteModal(false);
        setInviteTarget(null);
      } else {
        setToast(`Failed: ${data.error || "Unknown error"}`);
        setTimeout(() => setToast(""), 4000);
      }
    } catch (e) {
      setToast("Failed to send invite. Please try again.");
      setTimeout(() => setToast(""), 4000);
    }
    setInviteSending(false);
  };

  const emptyForm = { childFirst: "", childMiddle: "", childLast: "", dob: "", gender: "", address: "", allergies: "", medications: "", specialNeeds: "", physician: "", physicianPhone: "", insuranceProvider: "", insurancePolicy: "", parentName: "", parentRelation: "Mother", parentPhone: "", parentEmail: "", parentEmployer: "", parentWorkPhone: "", parent2Name: "", parent2Relation: "", parent2Phone: "", parent2Email: "", parent2Employer: "", parent2WorkPhone: "", emergencyContacts: [{ name: "", relation: "", phone: "" }], authorizedPickups: [{ name: "", relation: "" }], desiredStart: "", classroomPref: "", referralSource: "", previousSchool: "", status: "inquiry", priority: (apps || []).length + 1, notes: [], tourDate: "", tourComplete: false, immunizationOnFile: false, physicalOnFile: false };
  const [form, setForm] = useState(emptyForm);
  const [formTab, setFormTab] = useState("child");

  const openAdd = () => { setForm(emptyForm); setEditId(null); setFormTab("child"); setShowModal(true); };
  const openEdit = (app) => { setForm({ ...app, emergencyContacts: app.emergencyContacts?.length ? app.emergencyContacts : [{ name: "", relation: "", phone: "" }], authorizedPickups: app.authorizedPickups?.length ? app.authorizedPickups : [{ name: "", relation: "" }] }); setEditId(app.id); setFormTab("child"); setShowModal(true); };

  const save = () => {
    const errs = validate([
      { field: "childFirst", value: form.childFirst, checks: [required("Child's first name is required")] },
      { field: "childLast", value: form.childLast, checks: [required("Child's last name is required")] },
      { field: "parentName", value: form.parentName, checks: [required("Parent/guardian name is required")] },
      { field: "parentPhone", value: form.parentPhone, checks: [required("Parent phone is required"), optionalPhone()] },
      { field: "parentEmail", value: form.parentEmail, checks: [optionalEmail()] },
      { field: "parent2Email", value: form.parent2Email, checks: [optionalEmail()] },
      { field: "parent2Phone", value: form.parent2Phone, checks: [optionalPhone()] },
    ]);
    if (Object.keys(errs).length > 0) { setToast("Please fix the highlighted errors"); setTimeout(() => setToast(""), 3000); return; }
    if (editId) {
      setApps(p => p.map(a => a.id === editId ? { ...form, id: editId } : a));
    } else {
      setApps(p => [...p, { ...form, id: Date.now(), createdAt: new Date().toISOString() }]);
    }
    setShowModal(false);
    setToast(editId ? "Application updated" : "New inquiry added");
    setTimeout(() => setToast(""), 3000);
  };

  const moveStage = (appId, newStatus) => {
    setApps(p => p.map(a => a.id === appId ? { ...a, status: newStatus, tourComplete: newStatus === "tour_complete" || (newStatus === "application" && a.tourComplete) ? true : a.tourComplete } : a));
    const stage = ENROLLMENT_STAGES.find(s => s.id === newStatus);
    setToast(`Moved to ${stage?.label}`);
    setTimeout(() => setToast(""), 2500);
  };

  const addNote = (appId) => {
    if (!noteText.trim()) return;
    setApps(p => p.map(a => a.id === appId ? { ...a, notes: [...(a.notes || []), { text: noteText, date: new Date().toISOString().split("T")[0], author: currentUser.name }] } : a));
    setNoteText("");
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const deleteApp = (id) => { setApps(p => p.filter(a => a.id !== id)); setExpandedId(null); setConfirmDeleteId(null); setToast("Application removed"); setTimeout(() => setToast(""), 3000); };

  const enrollStudent = async (app) => {
    if (!setStudents) return;
    const newStudent = {
      id: Date.now(), firstName: app.childFirst, middleName: app.childMiddle || "", lastName: app.childLast,
      dob: app.dob, gender: app.gender, classroomId: app.classroomPref || classrooms[0]?.id,
      allergies: app.allergies, medications: app.medications, specialNeeds: app.specialNeeds,
      address: app.address,
      // Primary guardian
      guardianName: app.parentName, guardianEmail: app.parentEmail, guardianPhone: app.parentPhone,
      guardianRelation: app.parentRelation || "Parent", guardianEmployer: app.parentEmployer || "", guardianWorkPhone: app.parentWorkPhone || "",
      // Secondary guardian
      parent2Name: app.parent2Name || "", parent2Relation: app.parent2Relation || "", parent2Phone: app.parent2Phone || "",
      parent2Email: app.parent2Email || "", parent2Employer: app.parent2Employer || "", parent2WorkPhone: app.parent2WorkPhone || "",
      // Medical
      physician: app.physician, physicianPhone: app.physicianPhone,
      insuranceProvider: app.insuranceProvider || "", insurancePolicy: app.insurancePolicy || "",
      // Emergency & pickup
      emergencyContacts: app.emergencyContacts, authorizedPickups: app.authorizedPickups,
      // Enrollment date from desired start
      enrollmentDate: app.desiredStart || "",
      photo: null, tuitionPlanId: null
    };
    setStudents(p => [...p, newStudent]);
    if (setApps) {
      setApps(p => p.map(a => a.id === app.id ? { ...a, student_id: newStudent.id, status: "enrolled" } : a));
    }
    moveStage(app.id, "enrolled");
    setToast(`${app.childFirst} ${app.childLast} enrolled!`);
    setTimeout(() => setToast(""), 3000);

    // Auto-send parent invite if email is available
    if (app.parentEmail) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const res = await fetch(`${supabase.supabaseUrl}/functions/v1/invite-user`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
            body: JSON.stringify({ email: app.parentEmail, name: app.parentName || "Parent", role: "parent" }),
          });
          const data = await res.json();
          if (data.success) {
            setToast(`${app.childFirst} enrolled! Invite sent to ${app.parentEmail}`);
            setTimeout(() => setToast(""), 4000);
          }
        }
      } catch (e) {
        console.error("Auto-invite failed:", e);
        // Don't block enrollment if invite fails
      }
    }
  };

  const getAge = (dob) => {
    if (!dob) return "";
    const b = new Date(dob), now = new Date();
    const months = (now.getFullYear() - b.getFullYear()) * 12 + now.getMonth() - b.getMonth();
    return months < 12 ? `${months}mo` : `${Math.floor(months / 12)}y ${months % 12}m`;
  };

  const suggestClassroom = (dob) => {
    if (!dob) return null;
    const b = new Date(dob), now = new Date();
    const months = (now.getFullYear() - b.getFullYear()) * 12 + now.getMonth() - b.getMonth();
    const ageMap = [{ max: 12, group: "Infant" }, { max: 24, group: "Toddler" }, { max: 36, group: "Two's" }, { max: 48, group: "Preschool" }, { max: 60, group: "Pre-K" }, { max: 72, group: "VPK" }, { max: 999, group: "Kinder" }];
    const match = ageMap.find(a => months < a.max);
    return classrooms.find(c => c.ageGroup === match?.group) || null;
  };

  const activeApps = (apps || []).filter(a => a.status !== "withdrawn" && a.status !== "enrolled");
  const filtered = (apps || []).filter(a => {
    if (filter !== "all" && a.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return `${a.childFirst} ${a.childLast}`.toLowerCase().includes(s) || a.parentName?.toLowerCase().includes(s) || a.parentEmail?.toLowerCase().includes(s);
    }
    return true;
  }).sort((a, b) => a.priority - b.priority);

  const pipelineStages = ENROLLMENT_STAGES.filter(s => s.id !== "withdrawn" && s.id !== "enrolled");
  const stageCount = (stageId) => (apps || []).filter(a => a.status === stageId).length;

  const capacityInfo = (crId) => {
    const cr = classrooms.find(c => c.id === crId);
    if (!cr) return null;
    const enrolled = students.filter(s => s.classroomId === cr.id).length;
    const pending = (apps || []).filter(a => a.classroomPref === cr.id && a.status !== "enrolled" && a.status !== "withdrawn").length;
    return { name: cr.name, enrolled, capacity: cr.capacity, pending, available: cr.capacity - enrolled };
  };

  // Pipeline view
  const renderPipeline = () => (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? `repeat(${pipelineStages.length}, 260px)` : `repeat(${pipelineStages.length}, 1fr)`, gap: 12, overflowX: "auto", paddingBottom: isMobile ? 8 : 0 }}>
      {pipelineStages.map(stage => {
        const stageApps = (apps || []).filter(a => a.status === stage.id).sort((a, b) => a.priority - b.priority);
        return (
          <div key={stage.id} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (dragId && editable) moveStage(dragId, stage.id); setDragId(null); }}
            style={{ minWidth: 220, background: COLORS.bg, borderRadius: 14, border: `2px solid ${dragId ? stage.color + "44" : COLORS.borderLight}`, transition: "border-color 0.2s" }}>
            <div style={{ padding: "12px 14px 8px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `2px solid ${stage.color}33` }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: stage.color }}><Icon name={stage.icon} size={14} /> {stage.label}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: stage.color + "18", color: stage.color }}>{stageApps.length}</span>
            </div>
            <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 6, minHeight: 120 }}>
              {stageApps.map(app => {
                const cap = capacityInfo(app.classroomPref);
                return (
                  <div key={app.id} draggable={editable} onDragStart={() => setDragId(app.id)} onDragEnd={() => setDragId(null)}
                    onClick={() => setExpandedId(expandedId === app.id ? null : app.id)}
                    className="card-hover" style={{ padding: "10px 12px", borderRadius: 10, background: COLORS.card, border: `1px solid ${COLORS.border}`, cursor: editable ? "grab" : "pointer", opacity: dragId === app.id ? 0.5 : 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginBottom: 2 }}>{app.childFirst} {app.childLast}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>{getAge(app.dob)} old · {app.parentName}</div>
                    {cap && <div style={{ fontSize: 10, color: cap.available > 0 ? COLORS.accent : COLORS.danger, fontWeight: 600 }}>{cap.name}: {cap.available > 0 ? `${cap.available} spots open` : "Full"}</div>}
                    {app.tourDate && !app.tourComplete && <div style={{ fontSize: 10, color: COLORS.primary, fontWeight: 600, marginTop: 2 }}><Icon name="clock" size={10} /> Tour: {new Date(app.tourDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>}
                  </div>
                );
              })}
              {stageApps.length === 0 && <div style={{ fontSize: 12, color: COLORS.textMuted, textAlign: "center", padding: "24px 8px", fontStyle: "italic" }}>No applications</div>}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Detail panel for expanded application
  const renderDetail = (app) => {
    const cap = capacityInfo(app.classroomPref);
    const suggested = suggestClassroom(app.dob);
    const nextStage = (() => {
      const order = ["inquiry", "waitlist", "tour_scheduled", "tour_complete", "application", "enrolled"];
      const idx = order.indexOf(app.status);
      return idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
    })();
    const nextLabel = ENROLLMENT_STAGES.find(s => s.id === nextStage)?.label;

    return (
      <div className="anim-page" style={{ background: COLORS.card, borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: COLORS.navy }}>{app.childFirst} {app.childMiddle ? app.childMiddle + " " : ""}{app.childLast}</h3>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 2 }}>{getAge(app.dob)} old · DOB: {app.dob ? new Date(app.dob + "T12:00:00").toLocaleDateString() : "N/A"} · {app.gender || "N/A"}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <EnrollStatusBadge status={app.status} />
            {editable && <button onClick={() => openEdit(app)} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 11, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}><Icon name="edit" size={12} /> Edit</button>}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>
          {/* Parent/Guardian Info */}
          <div style={{ padding: 14, borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 8 }}>Parent / Guardian</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{app.parentName} <span style={{ fontWeight: 500, color: COLORS.textMuted }}>({app.parentRelation})</span></div>
            <div style={{ fontSize: 12, color: COLORS.text, marginTop: 4 }}><Icon name="phone" size={12} /> {app.parentPhone}</div>
            {app.parentEmail && <div style={{ fontSize: 12, color: COLORS.text }}><Icon name="mail" size={12} /> {app.parentEmail}</div>}
            {app.parentEmployer && <div style={{ fontSize: 12, color: COLORS.textMuted }}><Icon name="building" size={12} /> {app.parentEmployer} {app.parentWorkPhone ? `· ${app.parentWorkPhone}` : ""}</div>}
            {app.parent2Name && <>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy, marginTop: 10 }}>{app.parent2Name} <span style={{ fontWeight: 500, color: COLORS.textMuted }}>({app.parent2Relation})</span></div>
              <div style={{ fontSize: 12, color: COLORS.text }}><Icon name="phone" size={12} /> {app.parent2Phone}</div>
              {app.parent2Email && <div style={{ fontSize: 12, color: COLORS.text }}><Icon name="mail" size={12} /> {app.parent2Email}</div>}
              {app.parent2Employer && <div style={{ fontSize: 12, color: COLORS.textMuted }}><Icon name="building" size={12} /> {app.parent2Employer} {app.parent2WorkPhone ? `· ${app.parent2WorkPhone}` : ""}</div>}
            </>}
          </div>

          {/* Medical Info */}
          <div style={{ padding: 14, borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 8 }}>Medical / Health</div>
            <div style={{ fontSize: 12, color: COLORS.text }}><Icon name="stethoscope" size={12} /> {app.physician || "No physician listed"} {app.physicianPhone ? `· ${app.physicianPhone}` : ""}</div>
            {app.insuranceProvider && <div style={{ fontSize: 12, color: COLORS.text, marginTop: 4 }}><Icon name="audit" size={12} /> {app.insuranceProvider} · {app.insurancePolicy}</div>}
            {app.allergies && <div style={{ fontSize: 12, color: COLORS.danger, fontWeight: 600, marginTop: 6 }}><Icon name="alert" size={12} /> Allergies: {app.allergies}</div>}
            {app.medications && <div style={{ fontSize: 12, color: COLORS.warning, marginTop: 4 }}><Icon name="stethoscope" size={12} /> Medications: {app.medications}</div>}
            {app.specialNeeds && <div style={{ fontSize: 12, color: COLORS.primary, marginTop: 4 }}><Icon name="heart" size={12} /> Special Needs: {app.specialNeeds}</div>}
            <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
              <EnrollDocCheck editable={editable} label="DH 680 Immunization" checked={app.immunizationOnFile} onToggle={() => editable && setApps(p => p.map(a => a.id === app.id ? { ...a, immunizationOnFile: !a.immunizationOnFile } : a))} />
              <EnrollDocCheck editable={editable} label="Form 3040 Physical" checked={app.physicalOnFile} onToggle={() => editable && setApps(p => p.map(a => a.id === app.id ? { ...a, physicalOnFile: !a.physicalOnFile } : a))} />
            </div>
          </div>

          {/* Emergency & Pickup */}
          <div style={{ padding: 14, borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 8 }}>Emergency Contacts & Authorized Pickups</div>
            {(app.emergencyContacts || []).filter(c => c.name).map((c, i) => (
              <div key={i} style={{ fontSize: 12, color: COLORS.text, marginBottom: 4 }}><Icon name="alert" size={12} /> {c.name} ({c.relation}) · {c.phone}</div>
            ))}
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginTop: 10, marginBottom: 4 }}>Authorized Pickups</div>
            {(app.authorizedPickups || []).filter(c => c.name).map((c, i) => (
              <div key={i} style={{ fontSize: 12, color: COLORS.text, marginBottom: 2 }}><Icon name="check" size={11} /> {c.name} ({c.relation})</div>
            ))}
          </div>

          {/* Enrollment Details */}
          <div style={{ padding: 14, borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 8 }}>Enrollment Details</div>
            <div style={{ fontSize: 12, color: COLORS.text }}><Icon name="clock" size={12} /> Desired Start: {app.desiredStart ? new Date(app.desiredStart + "T12:00:00").toLocaleDateString() : "TBD"}</div>
            {cap && <div style={{ fontSize: 12, color: COLORS.text, marginTop: 4 }}><Icon name="classrooms" size={12} /> Preferred: {cap.name} ({cap.enrolled}/{cap.capacity} enrolled, {cap.available} available)</div>}
            {suggested && cap && suggested.id !== app.classroomPref && <div style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600, marginTop: 4 }}><Icon name="events" size={12} /> Age-suggested: {suggested.name}</div>}
            {app.referralSource && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}><Icon name="bell" size={12} /> Referral: {app.referralSource}</div>}
            {app.previousSchool && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}><Icon name="classrooms" size={12} /> Previous: {app.previousSchool}</div>}
            {app.address && <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}><Icon name="map-pin" size={12} /> {app.address}</div>}
          </div>
        </div>

        {/* Communication Log */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", marginBottom: 8 }}>Communication Log</div>
          <div style={{ maxHeight: 160, overflowY: "auto", marginBottom: 8 }}>
            {(app.notes || []).length === 0 && <div style={{ fontSize: 12, color: COLORS.textMuted, fontStyle: "italic" }}>No notes yet</div>}
            {(app.notes || []).slice().reverse().map((n, i) => (
              <div key={i} style={{ fontSize: 12, color: COLORS.text, padding: "6px 10px", marginBottom: 4, borderRadius: 8, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}` }}>
                <span style={{ fontWeight: 700 }}>{n.author}</span> <span style={{ color: COLORS.textMuted }}>· {n.date}</span>
                <div style={{ marginTop: 2 }}>{n.text}</div>
              </div>
            ))}
          </div>
          {editable && <div style={{ display: "flex", gap: 8 }}>
            <input value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === "Enter" && addNote(app.id)} placeholder="Add a note..." style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 12, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text }} />
            <Button onClick={() => addNote(app.id)}>Add Note</Button>
          </div>}
        </div>

        {/* Action Buttons */}
        {editable && app.status !== "enrolled" && app.status !== "withdrawn" && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {nextStage && nextStage !== "enrolled" && <Button onClick={() => moveStage(app.id, nextStage)}>→ Move to {nextLabel}</Button>}
            {app.status === "application" && <Button onClick={() => enrollStudent(app)} style={{ background: "#27ae60" }}><Icon name="students" size={14} /> Enroll Student</Button>}
            {app.parentEmail && <Button variant="secondary" onClick={() => openInvite(app)} style={{ color: COLORS.primary, borderColor: COLORS.primary }}><Icon name="mail" size={14} /> Send Parent Invite</Button>}
            <Button variant="secondary" onClick={() => moveStage(app.id, "withdrawn")} style={{ color: COLORS.textMuted }}><Icon name="arrow-left" size={14} /> Withdraw</Button>
            <Button variant="secondary" onClick={() => setConfirmDeleteId(app.id)} style={{ color: COLORS.danger }}><Icon name="trash" size={14} /> Delete</Button>
          </div>
        )}
        {app.status === "enrolled" && editable && app.parentEmail && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Button variant="secondary" onClick={() => openInvite(app)} style={{ color: COLORS.primary, borderColor: COLORS.primary }}><Icon name="mail" size={14} /> Send Parent Invite</Button>
            <Button variant="secondary" onClick={() => setConfirmDeleteId(app.id)} style={{ color: COLORS.danger }}><Icon name="trash" size={14} /> Delete</Button>
          </div>
        )}
        {app.status === "enrolled" && editable && !app.parentEmail && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setConfirmDeleteId(app.id)} style={{ color: COLORS.danger }}><Icon name="trash" size={14} /> Delete</Button>
          </div>
        )}
        {app.status === "withdrawn" && editable && (
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={() => moveStage(app.id, "inquiry")}><Icon name="coverage" size={14} /> Reactivate</Button>
            <Button variant="secondary" onClick={() => setConfirmDeleteId(app.id)} style={{ color: COLORS.danger }}><Icon name="trash" size={14} /> Delete</Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="anim-page" key="enrollment" style={{ padding: 24 }}>
      {toast && <div className="anim-toast" style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "12px 24px", borderRadius: 12, background: toast.startsWith("Failed") || toast.startsWith("Error") ? COLORS.danger : COLORS.success, color: "#fff", fontSize: 13, fontWeight: 700, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>{toast.startsWith("Failed") || toast.startsWith("Error") ? "✕" : "✓"} {toast}</div>}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}><FileText size={24} style={{display:"inline",verticalAlign:"middle",marginRight:8}} />Enrollment Pipeline</h2>
          <p style={{ margin: "4px 0 0", color: COLORS.textMuted, fontSize: 14 }}>Manage inquiries, waitlist, tours, and enrollment applications</p>
        </div>
        {editable && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {enrollmentFormUrl && (
              <button onClick={() => { setShowShareModal(true); setShareEmail(""); setShareSuccess(false); }} style={{ padding: "9px 16px", borderRadius: 10, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 12, fontWeight: 700, color: COLORS.primary, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
                <Icon name="mail" size={14} />
                Share Enrollment Form
              </button>
            )}
            <Button onClick={openAdd}><Icon name="plus" size={14} /> New Inquiry</Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12, marginBottom: 20 }}>
        {ENROLLMENT_STAGES.filter(s => s.id !== "withdrawn").map(stage => {
          const active = filter === stage.id;
          return (
          <div key={stage.id} className="card-hover" style={{ padding: "12px 16px", borderRadius: 12, background: active ? stage.color + "14" : COLORS.card, border: `1px solid ${active ? stage.color : COLORS.border}`, textAlign: "center", cursor: "pointer", borderTop: `3px solid ${stage.color}`, transition: "all 0.2s", transform: active ? "scale(1.03)" : "scale(1)" }}
            onClick={() => { const next = active ? "all" : stage.id; setFilter(next); if (next !== "all") setView("list"); }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: stage.color }}><AnimatedNumber value={stageCount(stage.id)} /></div>
            <div style={{ fontSize: 11, fontWeight: 700, color: active ? stage.color : COLORS.textMuted }}>{stage.label}</div>
          </div>);
        })}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 4, background: COLORS.card, borderRadius: 10, padding: 3, border: `1px solid ${COLORS.border}` }}>
          {[{ id: "pipeline", label: "Pipeline" }, { id: "list", label: "List" }].map(v => (
            <button key={v.id} onClick={() => setView(v.id)} style={{ padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", background: view === v.id ? COLORS.primary : "transparent", color: view === v.id ? "#fff" : COLORS.textMuted, transition: "all 0.15s" }}>{v.label}</button>
          ))}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by child or parent name..." style={{ flex: 1, minWidth: 200, padding: "9px 14px", borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.card, color: COLORS.text }} />
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.card, color: COLORS.text }}>
          <option value="all">All Stages</option>
          {ENROLLMENT_STAGES.map(s => <option key={s.id} value={s.id}>{s.label} ({stageCount(s.id)})</option>)}
        </select>
      </div>

      {/* Pipeline or List View */}
      {view === "pipeline" && renderPipeline()}
      {view === "list" && (
        <div style={{ background: COLORS.card, borderRadius: 14, border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}` }}>
              {["#", "Child Name", "Age", "Parent", "Status", "Preferred Classroom", "Desired Start", "Documents", ""].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map(app => {
                const cap = capacityInfo(app.classroomPref);
                return (
                  <tr key={app.id} className="row-hover anim-row" onClick={() => setExpandedId(expandedId === app.id ? null : app.id)} style={{ borderBottom: `1px solid ${COLORS.borderLight}`, cursor: "pointer", background: expandedId === app.id ? COLORS.primaryPale : "transparent" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: COLORS.textMuted }}>{app.priority}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: COLORS.navy }}>{app.childFirst} {app.childMiddle ? app.childMiddle[0] + ". " : ""}{app.childLast}</td>
                    <td style={{ padding: "10px 12px", color: COLORS.textMuted }}>{getAge(app.dob)}</td>
                    <td style={{ padding: "10px 12px" }}>{app.parentName}<div style={{ fontSize: 11, color: COLORS.textMuted }}>{app.parentPhone}</div></td>
                    <td style={{ padding: "10px 12px" }}><EnrollStatusBadge status={app.status} /></td>
                    <td style={{ padding: "10px 12px", fontSize: 12 }}>{cap ? cap.name : "—"}{cap ? <span style={{ color: cap.available > 0 ? COLORS.accent : COLORS.danger, fontSize: 10, fontWeight: 600, display: "block" }}>{cap.available > 0 ? `${cap.available} open` : "Full"}</span> : null}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12 }}>{app.desiredStart ? new Date(app.desiredStart + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "TBD"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <span title="Immunization" style={{ fontSize: 14, opacity: app.immunizationOnFile ? 1 : 0.3 }}><Icon name="stethoscope" size={14} /></span>
                        <span title="Physical" style={{ fontSize: 14, opacity: app.physicalOnFile ? 1 : 0.3 }}><Icon name="stethoscope" size={14} /></span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 16, color: COLORS.textMuted }}>{expandedId === app.id ? "▲" : "▼"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted }}>No applications found</div>}
        </div>
      )}

      {/* Expanded Detail Panel */}
      {expandedId && (apps || []).find(a => a.id === expandedId) && (
        <div style={{ marginTop: 16 }}>
          {renderDetail((apps || []).find(a => a.id === expandedId))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} title="Delete Application">
        {(() => { const app = (apps || []).find(a => a.id === confirmDeleteId); if (!app) return null; return (<div>
          <p style={{ fontSize: 14, color: COLORS.text, marginBottom: 16 }}>Are you sure you want to permanently remove this application?</p>
          <div style={{ padding: 14, borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}`, marginBottom: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.navy }}>{app.childFirst} {app.childMiddle ? app.childMiddle + " " : ""}{app.childLast}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Parent: {app.parentName} · {app.parentPhone}</div>
            <div style={{ marginTop: 6 }}><EnrollStatusBadge status={app.status} /></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Button variant="secondary" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button onClick={() => deleteApp(app.id)} style={{ background: COLORS.danger }}><Icon name="trash" size={14} /> Delete Application</Button>
          </div>
        </div>); })()}
      </Modal>

      {/* Add/Edit Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title={editId ? "Edit Application" : "New Inquiry"} wide>
        <div style={{ display: "flex", gap: 4, marginBottom: 16, background: COLORS.bg, borderRadius: 10, padding: 3 }}>
          {[{ id: "child", label: "Child", icon: "students" }, { id: "parent", label: "Parents", icon: "users" }, { id: "medical", label: "Medical", icon: "stethoscope" }, { id: "emergency", label: "Emergency", icon: "alert" }, { id: "enrollment", label: "Enrollment", icon: "daily-log" }].map(t => (
            <button key={t.id} onClick={() => setFormTab(t.id)} style={{ flex: 1, padding: "8px 6px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit", background: formTab === t.id ? COLORS.primary : "transparent", color: formTab === t.id ? "#fff" : COLORS.textMuted, transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}><Icon name={t.icon} size={12} />{t.label}</button>
          ))}
        </div>

        {formTab === "child" && <>
          <EnrollFormSection title="Child Information (Required by FL DCF)">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
              <InputField label="First Name *" value={form.childFirst} onChange={e => setForm(f => ({ ...f, childFirst: e.target.value }))} />
              <InputField label="Middle Name" value={form.childMiddle} onChange={e => setForm(f => ({ ...f, childMiddle: e.target.value }))} />
              <InputField label="Last Name *" value={form.childLast} onChange={e => setForm(f => ({ ...f, childLast: e.target.value }))} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Date of Birth" type="date" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} />
              <div><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Gender</label>
                <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.card, color: COLORS.text }}>
                  <option value="">Select...</option><option>Male</option><option>Female</option>
                </select>
              </div>
            </div>
            <InputField label="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Street, City, State, ZIP" />
          </EnrollFormSection>
        </>}

        {formTab === "parent" && <>
          <EnrollFormSection title="Primary Parent / Legal Guardian">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Full Name *" value={form.parentName} onChange={e => setForm(f => ({ ...f, parentName: e.target.value }))} />
              <div><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Relationship</label>
                <select value={form.parentRelation} onChange={e => setForm(f => ({ ...f, parentRelation: e.target.value }))} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.card, color: COLORS.text }}>
                  <option>Mother</option><option>Father</option><option>Legal Guardian</option><option>Grandparent</option><option>Other</option>
                </select>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Phone *" value={form.parentPhone} onChange={e => setForm(f => ({ ...f, parentPhone: e.target.value }))} placeholder="(305) 555-0123" />
              <InputField label="Email" value={form.parentEmail} onChange={e => setForm(f => ({ ...f, parentEmail: e.target.value }))} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Employer" value={form.parentEmployer} onChange={e => setForm(f => ({ ...f, parentEmployer: e.target.value }))} />
              <InputField label="Work Phone" value={form.parentWorkPhone} onChange={e => setForm(f => ({ ...f, parentWorkPhone: e.target.value }))} />
            </div>
          </EnrollFormSection>
          <EnrollFormSection title="Second Parent / Guardian (Optional)">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Full Name" value={form.parent2Name} onChange={e => setForm(f => ({ ...f, parent2Name: e.target.value }))} />
              <div><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Relationship</label>
                <select value={form.parent2Relation} onChange={e => setForm(f => ({ ...f, parent2Relation: e.target.value }))} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.card, color: COLORS.text }}>
                  <option value="">Select...</option><option>Father</option><option>Mother</option><option>Legal Guardian</option><option>Grandparent</option><option>Other</option>
                </select>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Phone" value={form.parent2Phone} onChange={e => setForm(f => ({ ...f, parent2Phone: e.target.value }))} />
              <InputField label="Email" value={form.parent2Email} onChange={e => setForm(f => ({ ...f, parent2Email: e.target.value }))} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Employer" value={form.parent2Employer} onChange={e => setForm(f => ({ ...f, parent2Employer: e.target.value }))} />
              <InputField label="Work Phone" value={form.parent2WorkPhone} onChange={e => setForm(f => ({ ...f, parent2WorkPhone: e.target.value }))} />
            </div>
          </EnrollFormSection>
        </>}

        {formTab === "medical" && <>
          <EnrollFormSection title="Medical Information (Required by FL DCF § 65C-22)">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Physician Name" value={form.physician} onChange={e => setForm(f => ({ ...f, physician: e.target.value }))} />
              <InputField label="Physician Phone" value={form.physicianPhone} onChange={e => setForm(f => ({ ...f, physicianPhone: e.target.value }))} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Insurance Provider" value={form.insuranceProvider} onChange={e => setForm(f => ({ ...f, insuranceProvider: e.target.value }))} />
              <InputField label="Policy Number" value={form.insurancePolicy} onChange={e => setForm(f => ({ ...f, insurancePolicy: e.target.value }))} />
            </div>
            <InputField label="Allergies (food, medicine, insect bites, etc.)" value={form.allergies} onChange={e => setForm(f => ({ ...f, allergies: e.target.value }))} placeholder="e.g., Peanuts, Penicillin, Bee stings" />
            <InputField label="Regular Medications" value={form.medications} onChange={e => setForm(f => ({ ...f, medications: e.target.value }))} placeholder="Name, dosage, schedule" />
            <InputField label="Special Needs / Conditions" value={form.specialNeeds} onChange={e => setForm(f => ({ ...f, specialNeeds: e.target.value }))} placeholder="e.g., Speech therapy, asthma action plan" />
          </EnrollFormSection>
          <EnrollFormSection title="Required Documents (per FL DCF Handbook §7.1-7.2)">
            <div style={{ padding: 12, borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}` }}>
              <p style={{ fontSize: 12, color: COLORS.textMuted, margin: "0 0 10px" }}>Florida DCF requires a current physical exam (Form 3040) and immunization record (DH 680/681) within 30 days of enrollment.</p>
              <div style={{ display: "flex", gap: 20 }}>
                <EnrollDocCheck editable={true} label="DH 680 Immunization Record" checked={form.immunizationOnFile} onToggle={() => setForm(f => ({ ...f, immunizationOnFile: !f.immunizationOnFile }))} />
                <EnrollDocCheck editable={true} label="Form 3040 Physical Exam" checked={form.physicalOnFile} onToggle={() => setForm(f => ({ ...f, physicalOnFile: !f.physicalOnFile }))} />
              </div>
            </div>
          </EnrollFormSection>
        </>}

        {formTab === "emergency" && <>
          <EnrollFormSection title="Emergency Contacts">
            {form.emergencyContacts.map((ec, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 10, marginBottom: 8 }}>
                <InputField label={i === 0 ? "Name" : ""} value={ec.name} onChange={e => { const ecs = [...form.emergencyContacts]; ecs[i] = { ...ecs[i], name: e.target.value }; setForm(f => ({ ...f, emergencyContacts: ecs })); }} placeholder="Full name" />
                <InputField label={i === 0 ? "Relation" : ""} value={ec.relation} onChange={e => { const ecs = [...form.emergencyContacts]; ecs[i] = { ...ecs[i], relation: e.target.value }; setForm(f => ({ ...f, emergencyContacts: ecs })); }} placeholder="e.g., Grandmother" />
                <InputField label={i === 0 ? "Phone" : ""} value={ec.phone} onChange={e => { const ecs = [...form.emergencyContacts]; ecs[i] = { ...ecs[i], phone: e.target.value }; setForm(f => ({ ...f, emergencyContacts: ecs })); }} placeholder="(305) 555-0123" />
                <button onClick={() => setForm(f => ({ ...f, emergencyContacts: f.emergencyContacts.filter((_, j) => j !== i) }))} style={{ padding: "8px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 14, color: COLORS.danger, marginTop: i === 0 ? 22 : 0 }}>×</button>
              </div>
            ))}
            <button onClick={() => setForm(f => ({ ...f, emergencyContacts: [...f.emergencyContacts, { name: "", relation: "", phone: "" }] }))} style={{ padding: "6px 14px", borderRadius: 8, border: `1px dashed ${COLORS.border}`, background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit" }}>+ Add Contact</button>
          </EnrollFormSection>
          <EnrollFormSection title="Authorized Pickups (Child released only to parent/guardian and persons listed)">
            {form.authorizedPickups.map((ap, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, marginBottom: 8 }}>
                <InputField label={i === 0 ? "Name" : ""} value={ap.name} onChange={e => { const aps = [...form.authorizedPickups]; aps[i] = { ...aps[i], name: e.target.value }; setForm(f => ({ ...f, authorizedPickups: aps })); }} placeholder="Full name" />
                <InputField label={i === 0 ? "Relation" : ""} value={ap.relation} onChange={e => { const aps = [...form.authorizedPickups]; aps[i] = { ...aps[i], relation: e.target.value }; setForm(f => ({ ...f, authorizedPickups: aps })); }} placeholder="e.g., Aunt" />
                <button onClick={() => setForm(f => ({ ...f, authorizedPickups: f.authorizedPickups.filter((_, j) => j !== i) }))} style={{ padding: "8px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.card, cursor: "pointer", fontSize: 14, color: COLORS.danger, marginTop: i === 0 ? 22 : 0 }}>×</button>
              </div>
            ))}
            <button onClick={() => setForm(f => ({ ...f, authorizedPickups: [...f.authorizedPickups, { name: "", relation: "" }] }))} style={{ padding: "6px 14px", borderRadius: 8, border: `1px dashed ${COLORS.border}`, background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: COLORS.primary, fontFamily: "inherit" }}>+ Add Person</button>
          </EnrollFormSection>
        </>}

        {formTab === "enrollment" && <>
          <EnrollFormSection title="Enrollment Preferences">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Desired Start Date" type="date" value={form.desiredStart} onChange={e => setForm(f => ({ ...f, desiredStart: e.target.value }))} />
              <div><label style={{ display: "block", fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Preferred Classroom</label>
                <select value={form.classroomPref} onChange={e => setForm(f => ({ ...f, classroomPref: e.target.value || "" }))} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.card, color: COLORS.text }}>
                  <option value="">Select classroom...</option>
                  {classrooms.map(c => { const enrolled = students.filter(s => s.classroomId === c.id).length; return <option key={c.id} value={c.id}>{c.name} ({enrolled}/{c.capacity})</option>; })}
                </select>
                {form.dob && suggestClassroom(form.dob) && <div style={{ fontSize: 11, color: COLORS.primary, fontWeight: 600, marginTop: 4 }}><Icon name="events" size={12} /> Age-suggested: {suggestClassroom(form.dob).name}</div>}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Referral Source" value={form.referralSource} onChange={e => setForm(f => ({ ...f, referralSource: e.target.value }))} placeholder="e.g., Google, Friend, Drive-by" />
              <InputField label="Previous School" value={form.previousSchool} onChange={e => setForm(f => ({ ...f, previousSchool: e.target.value }))} placeholder="If applicable" />
            </div>
          </EnrollFormSection>
          <EnrollFormSection title="Tour Scheduling">
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
              <InputField label="Tour Date" type="date" value={form.tourDate} onChange={e => setForm(f => ({ ...f, tourDate: e.target.value }))} />
              <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                  <input type="checkbox" checked={form.tourComplete} onChange={e => setForm(f => ({ ...f, tourComplete: e.target.checked }))} style={{ accentColor: COLORS.accent, width: 16, height: 16 }} />
                  Tour completed
                </label>
              </div>
            </div>
          </EnrollFormSection>
          <EnrollFormSection title="Application Status">
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.card, color: COLORS.text }}>
              {ENROLLMENT_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </EnrollFormSection>
        </>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12, borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={save}>{editId ? "Save Changes" : "Add Inquiry"}</Button>
        </div>
      </Modal>

      {/* Share Enrollment Form Modal */}
      {showShareModal && (
        <Modal open={showShareModal} onClose={() => { setShowShareModal(false); setShareEmail(""); setShareSuccess(false); }} title="Share Enrollment Form">
          <p style={{ margin: "0 0 20px", fontSize: 14, color: COLORS.textMuted, lineHeight: 1.6 }}>
            Send a link to the enrollment form directly to a family's email. They'll be able to fill it out and submit it — no account needed.
          </p>
          {shareSuccess ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ marginBottom: 8 }}><Icon name="mail" size={36} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.success }}>Sent successfully!</div>
            </div>
          ) : (
            <>
              <InputField
                label="Family Email Address"
                type="email"
                value={shareEmail}
                onChange={e => setShareEmail(e.target.value)}
                placeholder="parent@email.com"
              />
              <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.bg, border: `1px solid ${COLORS.borderLight}`, marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, marginBottom: 4, textTransform: "uppercase" }}>Form Link</div>
                <div style={{ fontSize: 12, color: COLORS.primary, wordBreak: "break-all" }}>{enrollmentFormUrl}</div>
                <button onClick={copyEnrollLink} style={{ marginTop: 6, background: "none", border: "none", color: COLORS.textMuted, fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, padding: 0 }}>
                  <Icon name="copy" size={11} /> {linkCopied ? "Copied!" : "Copy link"}
                </button>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <Button variant="secondary" onClick={() => setShowShareModal(false)}>Cancel</Button>
                <Button onClick={sendShareEmail} disabled={shareSending || !shareEmail.trim()}>
                  {shareSending ? "Sending…" : <><Icon name="mail" size={14} /> Send Form Link</>}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* Send Parent Invite Modal */}
      {showInviteModal && inviteTarget && (
        <Modal open={showInviteModal} onClose={() => { setShowInviteModal(false); setInviteTarget(null); }} title="Send Parent Invite">
          <p style={{ margin: "0 0 16px", fontSize: 14, color: COLORS.textMuted, lineHeight: 1.6 }}>
            Send an invitation email to the parent/guardian of <strong>{inviteTarget.childName}</strong>. They'll receive a link to create their account and will be able to see their child in the parent portal.
          </p>
          <InputField
            label="Parent Name"
            value={inviteTarget.name}
            onChange={e => setInviteTarget(p => ({ ...p, name: e.target.value }))}
            placeholder="Full name"
          />
          <InputField
            label="Parent Email"
            type="email"
            value={inviteTarget.email}
            onChange={e => setInviteTarget(p => ({ ...p, email: e.target.value }))}
            placeholder="parent@email.com"
          />
          <div style={{ padding: "10px 14px", borderRadius: 10, background: COLORS.primaryPale, fontSize: 12, color: COLORS.primary, fontWeight: 600, marginBottom: 16 }}>
            <Icon name="mail" size={12} /> The parent will receive a branded email with a signup link. Once they sign up, they'll immediately see their child in the parent portal.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => { setShowInviteModal(false); setInviteTarget(null); }}>Cancel</Button>
            <Button onClick={sendInvite} disabled={inviteSending || !inviteTarget.email || !inviteTarget.name}>
              {inviteSending ? "Sending…" : <><Icon name="mail" size={14} /> Send Invite</>}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
});

export default EnrollmentPage;
